import { createMemo, createSignal, For, onMount, Show, untrack } from "solid-js";
import {
  compter,
  type Compte,
  type DefinitionFacette,
  type IdFacette,
  type Index,
  type Mode,
  type PointCommun,
  type Selection,
} from "../domaine/facettes";
import { normaliser } from "../domaine/filtres";
import { pluriel } from "../format";
import { Icone, type NomIcone } from "./Icone";

/** Valeurs montrées d'emblée par facette ; le reste derrière « afficher tout ». */
const APERCU = 8;
/** Au-delà, la facette a son propre champ de filtre. */
const SEUIL_RECHERCHE = 12;

/**
 * Les facettes rangées en rubriques, chacune avec son icône : des repères pour
 * trouver d'un coup d'œil où chercher dans une longue liste.
 */
const RUBRIQUES: { titre: string; icone: NomIcone; facettes: IdFacette[] }[] = [
  { titre: "Bioagresseurs", icone: "insecte", facettes: ["classe", "bioagresseur"] },
  { titre: "Leviers", icone: "levier", facettes: ["levier"] },
  {
    titre: "Cultures et filières",
    icone: "feuille",
    facettes: ["culture", "filiere", "filiereImpliquee", "plan"],
  },
  {
    titre: "Acteurs",
    icone: "personnes",
    facettes: ["porteur", "typePorteur", "partenaire", "typePartenaire"],
  },
  { titre: "Avancement", icone: "horloge", facettes: ["etat"] },
];

/** Teinte d'une valeur, quand elle en a une ailleurs (les classes de bioagresseur). */
export type Teinte = (id: IdFacette, cle: string) => string | undefined;

export const nombreCriteres = (s: Selection) =>
  Object.values(s).reduce((n, c) => n + (c?.valeurs.length ?? 0), 0);

/**
 * Le menu des filtres, déroulé sous son bouton par-dessus la vue : les facettes
 * l'une sous l'autre, puis ce que partagent les projets retenus, cliquable. Rien
 * ne s'y insère au-dessus de ce qu'on coche : la liste ne saute pas sous le pointeur.
 */
export function VoletFiltres(props: {
  facettes: DefinitionFacette[];
  index: Index;
  selection: Selection;
  communs: PointCommun[];
  nombreRetenus: number;
  libelleFacette: (id: IdFacette) => string;
  teinte: Teinte;
  surBascule: (id: IdFacette, cle: string) => void;
  surMode: (id: IdFacette, mode: Mode) => void;
  surEffacer: () => void;
}) {
  const [recherche, setRecherche] = createSignal("");
  let champ: HTMLInputElement | undefined;
  // On ouvre le menu pour chercher : le champ attend la frappe.
  onMount(() => champ?.focus());

  const definition = (id: IdFacette) => props.facettes.find((f) => f.id === id);
  const rubriques = () =>
    RUBRIQUES.map((r) => ({
      ...r,
      facettes: r.facettes.flatMap((id) => definition(id) ?? []),
    })).filter((r) => r.facettes.length);
  /** Les valeurs de toutes les facettes qui contiennent le texte cherché. */
  const trouvees = createMemo(() => {
    const q = normaliser(recherche());
    if (!q) return null;
    return rubriques()
      .flatMap((r) =>
        r.facettes.map((f) => ({
          rubrique: r,
          facette: f,
          valeurs: compter(props.index, props.selection, f.id).filter((c) =>
            normaliser(c.libelle).includes(q),
          ),
        })),
      )
      .filter((g) => g.valeurs.length);
  });
  /** Points communs rangés par facette, dans l'ordre des facettes. */
  const groupesCommuns = createMemo(() => {
    const groupes = new Map<IdFacette, PointCommun[]>();
    for (const c of props.communs) groupes.set(c.facette, [...(groupes.get(c.facette) ?? []), c]);
    return [...groupes.entries()];
  });

  return (
    <div class="volet-filtres" id="volet-filtres" role="region" aria-label="Filtres">
      <div class="volet-tete">
        <span class="champ-recherche volet-recherche">
          <Icone nom="loupe" />
          <input
            ref={(e) => (champ = e)}
            type="search"
            aria-label="Rechercher un filtre"
            placeholder="Rechercher un filtre…"
            value={recherche()}
            onInput={(e) => setRecherche(e.currentTarget.value)}
          />
        </span>
        <span class="lecture">
          {nombreCriteres(props.selection)
            ? `${pluriel(nombreCriteres(props.selection), "filtre")} · ${pluriel(props.nombreRetenus, "projet")}`
            : "Aucun filtre"}
        </span>
        <button
          type="button"
          class="lien-projet"
          disabled={!nombreCriteres(props.selection)}
          onClick={() => props.surEffacer()}
        >
          Effacer les filtres
        </button>
      </div>
      <Show
        when={trouvees()}
        fallback={
          <For each={rubriques()}>
            {(r) => (
              <section class="rubrique">
                <h3 class="rubrique-titre">
                  <Icone nom={r.icone} />
                  {r.titre}
                </h3>
                <For each={r.facettes}>
                  {(f) => (
                    <Facette
                      definition={f}
                      index={props.index}
                      selection={props.selection}
                      teinte={(cle) => props.teinte(f.id, cle)}
                      surBascule={(cle) => props.surBascule(f.id, cle)}
                      surMode={(mode) => props.surMode(f.id, mode)}
                    />
                  )}
                </For>
              </section>
            )}
          </For>
        }
      >
        {(groupes) => (
          <Show
            when={groupes().length}
            fallback={<p class="explication discret">Aucun filtre ne correspond.</p>}
          >
            <For each={groupes()}>
              {(g) => (
                <section class="rubrique">
                  <h3 class="rubrique-titre">
                    <Icone nom={g.rubrique.icone} />
                    {g.facette.libelle}
                  </h3>
                  <ul class="facette-valeurs">
                    <For each={g.valeurs}>
                      {(c) => (
                        <ValeurFiltre
                          compte={c}
                          teinte={props.teinte(g.facette.id, c.cle)}
                          surBascule={() => props.surBascule(g.facette.id, c.cle)}
                        />
                      )}
                    </For>
                  </ul>
                </section>
              )}
            </For>
          </Show>
        )}
      </Show>
      <Show when={!trouvees() && props.communs.length}>
        <div class="points-communs">
          <h3>En commun à ces {props.nombreRetenus} projets</h3>
          <dl class="faits">
            <For each={groupesCommuns()}>
              {([id, valeurs]) => (
                <>
                  <dt>{props.libelleFacette(id)}</dt>
                  <dd>
                    <ul class="etiquettes etiquettes-petites">
                      <For each={valeurs}>
                        {(c) => (
                          <li>
                            <button
                              type="button"
                              class="etiquette-bouton"
                              title={`Filtrer aussi sur « ${c.libelle} »`}
                              onClick={() => props.surBascule(c.facette, c.cle)}
                            >
                              {c.libelle}
                            </button>
                          </li>
                        )}
                      </For>
                    </ul>
                  </dd>
                </>
              )}
            </For>
          </dl>
        </div>
      </Show>
    </div>
  );
}

/** Une facette : ses valeurs à cocher avec leur effectif, repliable. */
function Facette(props: {
  definition: DefinitionFacette;
  index: Index;
  selection: Selection;
  teinte: (cle: string) => string | undefined;
  surBascule: (cle: string) => void;
  surMode: (mode: Mode) => void;
}) {
  const id = () => props.definition.id;
  const critere = () => props.selection[id()];
  const coches = () => critere()?.valeurs.length ?? 0;
  // Dépliée d'emblée si elle a des coches, puis au gré du lecteur : on replie aussi
  // une facette active, sa pastille dit qu'elle filtre.
  const [ouverte, setOuverte] = createSignal(untrack(coches) > 0);
  const [filtre, setFiltre] = createSignal("");
  const [tout, setTout] = createSignal(false);

  const comptes = createMemo(() => compter(props.index, props.selection, id()));
  const visibles = createMemo(() => {
    const q = normaliser(filtre());
    const liste = q ? comptes().filter((c) => normaliser(c.libelle).includes(q)) : comptes();
    if (tout() || q) return liste;
    // L'aperçu garde les cochées qui le dépassent, à la suite, pour qu'on puisse les décocher.
    return liste.filter((c, i) => i < APERCU || c.coche);
  });
  const caches = () => (filtre() ? 0 : comptes().length - visibles().length);
  const ouvert = ouverte;

  return (
    <div class="facette" classList={{ active: coches() > 0 }}>
      <button
        type="button"
        class="facette-tete"
        aria-expanded={ouvert()}
        onClick={() => setOuverte(!ouvert())}
      >
        <span class="facette-chevron" aria-hidden="true">
          {ouvert() ? "▾" : "▸"}
        </span>
        <span class="facette-nom">{props.definition.libelle}</span>
        <Show when={coches()}>
          <span class="facette-compte">{coches()}</span>
        </Show>
      </button>
      <Show when={ouvert()}>
        <div class="facette-corps">
          {/* Toujours là, grisé tant qu'il n'y a pas deux coches : sa venue décalerait la liste. */}
          <Show when={props.definition.multiple}>
            <div class="onglets onglets-petits facette-mode" role="group" aria-label="Combiner">
              <button
                type="button"
                class="onglet"
                classList={{ actif: critere()?.mode !== "tous" }}
                aria-pressed={critere()?.mode !== "tous"}
                disabled={coches() < 2}
                onClick={() => props.surMode("un")}
                title="Union : au moins une des valeurs cochées"
              >
                <Icone nom="union" />
                Au moins un
              </button>
              <button
                type="button"
                class="onglet"
                classList={{ actif: critere()?.mode === "tous" }}
                aria-pressed={critere()?.mode === "tous"}
                disabled={coches() < 2}
                onClick={() => props.surMode("tous")}
                title="Intersection : toutes les valeurs cochées"
              >
                <Icone nom="intersection" />
                Tous
              </button>
            </div>
          </Show>
          <Show when={comptes().length > SEUIL_RECHERCHE}>
            <input
              type="search"
              class="facette-filtre"
              aria-label={`Rechercher : ${props.definition.libelle}`}
              placeholder="Rechercher…"
              value={filtre()}
              onInput={(e) => setFiltre(e.currentTarget.value)}
            />
          </Show>
          <ul class="facette-valeurs">
            <For each={visibles()}>
              {(c) => (
                <ValeurFiltre
                  compte={c}
                  teinte={props.teinte(c.cle)}
                  surBascule={() => props.surBascule(c.cle)}
                />
              )}
            </For>
          </ul>
          <Show when={caches() > 0 || tout()}>
            <button type="button" class="lien-projet facette-plus" onClick={() => setTout(!tout())}>
              {tout() ? "Réduire" : `Afficher les ${caches()} autres`}
            </button>
          </Show>
        </div>
      </Show>
    </div>
  );
}

/** Une valeur à cocher, avec sa teinte s'il y a lieu et son effectif. */
function ValeurFiltre(props: {
  compte: Compte;
  teinte: string | undefined;
  surBascule: () => void;
}) {
  return (
    <li>
      <label class="facette-valeur" classList={{ vide: props.compte.n === 0 }}>
        <input type="checkbox" checked={props.compte.coche} onChange={() => props.surBascule()} />
        <Show when={props.teinte}>
          {(fond) => <span class="pastille" style={{ background: fond() }} />}
        </Show>
        <span class="facette-libelle">{props.compte.libelle}</span>
        <span class="facette-n">{props.compte.n}</span>
      </label>
    </li>
  );
}
